import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import { integrationAPI, warehouseAPI } from '../api';
import toast from 'react-hot-toast';
import { RecipesPage } from './Technolog';
import SubstitutesPage from './Substitutes';
import { openCard, openDivider } from '../components/openCard';
import ExchangeJournal from '../components/ExchangeJournal';

// ─── Рабочее место бухгалтера ────────────────────────────────────────────────
//
// Бухгалтер ведёт этот же завод в 1С. Его вопрос к MES всегда один: «почему у
// меня не сходится». Поэтому здесь не сводки и не графики, а три вещи, из
// которых расхождение и берётся:
//
//   1. Обмен с 1С — что встало и кто это чинит. Половину чинит сам бухгалтер:
//      сопоставить контрагента, завести склад.
//   2. Рецепты — из чего сложилась себестоимость листа. Технолог знает, какая
//      плёнка ложится на плиту; бухгалтер знает, что за лист ушло по факту.
//      Право одинаковое: завести, изменить, снять.
//   3. Остатки — с кодами 1С и по складам, минусом наверх.
//
// Документы он не заводит: они рождаются в цехе и в 1С. Смотреть — везде.

const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 12, padding: 18,
};

const LEVEL = {
  error: { color: '#ef4444', bg: '#ef444414', icon: '✕' },
  warn:  { color: '#f59e0b', bg: '#f59e0b14', icon: '!' },
};

const fmtQty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

// ─── Обмен с 1С: что встало и что с этим делать ──────────────────────────────
function ExchangeSection({ onGo }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    integrationAPI.getHealth()
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('Не удалось получить состояние обмена')))
      .finally(() => setLoading(false));
  }, []);

  // Обмен идёт сам по себе, без нашего участия — экран должен догонять его, а
  // не ждать, пока человек нажмёт «обновить».
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>;
  if (!data)   return null;

  const q  = data.queue || {};
  const ok = data.ok;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Одна строка сверху: связь есть или нет */}
      <div style={{
        ...CARD,
        borderLeft: `4px solid ${ok ? '#22c55e' : (data.problems.some(p => p.level === 'error') ? '#ef4444' : '#f59e0b')}`,
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 30 }}>{ok ? '✅' : '⚠'}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ color: 'var(--text)', fontWeight: 800, fontSize: 16 }}>
            {ok ? t('Обмен с 1С в порядке') : t('Обмен с 1С требует внимания')}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 3 }}>
            {t('Последняя связь с 1С')}: {fmtWhen(data.last_contact)}
            {data.silent_hours != null && data.silent_hours >= 1
              ? ` · ${t('это')} ${Math.round(data.silent_hours)} ${t('ч назад')}`
              : ''}
          </div>
        </div>
        <button onClick={load} style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
          color: 'var(--muted)', padding: '8px 16px', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>↻ {t('Обновить')}</button>
      </div>

      {/* Лента: что именно ушло и что пришло. Раньше на этот вопрос экран не
          отвечал вовсе — были только счётчики, а «дошла ли моя накладная»
          спрашивают именно про документ. */}
      <ExchangeJournal />

      {/* Очередь — цифрами, мелко: это фон, а не задача */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {[
          { l: t('Ждут отправки в 1С'), v: q.pending, c: 'var(--text)' },
          { l: t('Ушло в 1С'),          v: q.sent,    c: '#22c55e' },
          { l: t('Не принято'),          v: q.failed,  c: '#ef4444' },
          { l: t('Принято от 1С'),       v: q.inbox,   c: 'var(--text)' },
        ].map(s => (
          <div key={s.l} style={{ ...CARD, flex: 1, minWidth: 140, padding: 14 }}>
            <div style={{ color: 'var(--dim)', fontSize: 12 }}>{s.l}</div>
            <div style={{ color: s.c, fontSize: 24, fontWeight: 800, marginTop: 4 }}>
              {s.v ?? '—'}
            </div>
          </div>
        ))}
      </div>

      {ok && (
        <div style={{ ...CARD, textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👌</div>
          <div style={{ color: 'var(--text)', fontWeight: 700 }}>
            {t('Разбирать нечего')}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6 }}>
            {t('Всё, что цех сделал, ушло в 1С. Ничего не ждёт вашего вмешательства.')}
          </div>
        </div>
      )}

      {/* Каждая беда — своей карточкой: что, из-за чего, что сделать */}
      {(data.problems || []).map(p => {
        const L = LEVEL[p.level] || LEVEL.warn;
        return (
          <div key={p.key} style={{
            background: L.bg, border: `1px solid ${L.color}40`,
            borderRadius: 12, padding: 18,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{
                width: 22, height: 22, borderRadius: 11, background: L.color, color: '#fff',
                display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 800,
                flexShrink: 0,
              }}>{L.icon}</span>
              <span style={{ color: L.color, fontWeight: 800, fontSize: 15 }}>{p.title}</span>
              {p.count > 0 && p.key !== 'silence' && (
                <span style={{
                  background: L.color, color: '#fff', borderRadius: 20,
                  fontSize: 12, fontWeight: 700, padding: '2px 10px',
                }}>{p.count}</span>
              )}
            </div>

            <div style={{ color: 'var(--text)', fontSize: 13.5, lineHeight: 1.6, marginTop: 10 }}>
              {p.why}
            </div>
            <div style={{
              color: 'var(--text)', fontSize: 13.5, lineHeight: 1.6, marginTop: 10,
              background: 'var(--card)', borderRadius: 9, padding: '10px 13px',
            }}>
              <b>{t('Что сделать')}:</b> {p.todo}
            </div>

            {p.section && (
              <button onClick={() => onGo(p.section)} style={{
                marginTop: 12, background: L.color, border: 'none', borderRadius: 8,
                color: '#fff', padding: '9px 18px', fontSize: 13.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
                {p.section === 'recipes' ? t('Открыть рецепты →') : t('Открыть остатки →')}
              </button>
            )}

            {(p.items || []).length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {p.items.slice(0, 12).map((it, i) => (
                  <div key={i} style={{
                    background: 'var(--card)', borderRadius: 8, padding: '8px 12px',
                    fontSize: 13, color: 'var(--text)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ minWidth: 0, wordBreak: 'break-word' }}>
                        {it.name || `${it.entity} · ${it.local_id}`}
                      </span>
                      <span style={{ color: 'var(--dim)', whiteSpace: 'nowrap' }}>
                        {it.quantity != null ? fmtQty(it.quantity)
                          : it.events != null ? `${it.events} ${t('соб.')}`
                          : fmtWhen(it.created_at)}
                      </span>
                    </div>
                    {it.error && (
                      <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
                        {it.error}
                      </div>
                    )}
                  </div>
                ))}
                {p.items.length > 12 && (
                  <div style={{ color: 'var(--dim)', fontSize: 12 }}>
                    {t('и ещё')} {p.items.length - 12}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Остатки под сверку с 1С ─────────────────────────────────────────────────
//
// Сводка склада отвечает цеху на «хватит ли». Здесь другой вопрос: «почему у
// меня в 1С другое число». Поэтому видно код 1С и разрез по складам — по
// названию склад в 1С не найти, названия там правят, GUID остаётся.
const CATS = [
  { key: '',      label: 'Все' },
  { key: 'ldsp',  label: 'ЛДСП' },
  { key: 'dsp',   label: 'ДСП' },
  { key: 'paper', label: 'Бумага / Плёнка' },
  { key: 'other', label: 'Прочее' },
];
const SHOWS = [
  { key: 'nonzero',  label: 'С остатком' },
  { key: 'negative', label: 'Только минус' },
  { key: 'all',      label: 'Все позиции' },
];

function BalancesSection() {
  const [rows, setRows]       = useState([]);
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [cat, setCat]         = useState('');
  const [show, setShow]       = useState('nonzero');
  const [q, setQ]             = useState('');
  const [sent, setSent]       = useState('');     // что реально ушло на сервер
  const [openRow, setOpenRow] = useState(null);

  // Поиск на сервере, но не на каждую букву: справочник за тысячу позиций, и
  // запрос на каждое нажатие превращает набор слова в десяток запросов.
  useEffect(() => {
    const id = setTimeout(() => setSent(q.trim()), 400);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    warehouseAPI.getBalances({ category: cat, show, q: sent })
      .then(({ data }) => { setRows(data.results || []); setCount(data.count || 0); })
      .catch(() => toast.error(t('Не удалось загрузить остатки')))
      .finally(() => setLoading(false));
  }, [cat, show, sent]);

  const chip = (on) => ({
    background: on ? 'var(--accent)' : 'var(--bg)',
    border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
    borderRadius: 8, color: on ? '#fff' : 'var(--muted)',
    padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
    fontFamily: 'inherit',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, maxWidth: 820 }}>
        {t('Остатки MES так, как их видит 1С: с кодом позиции, с кодом склада и '
          + 'разрезом по складам. Минус — сверху: он и означает, что списано больше, '
          + 'чем приходовано, и с 1С такая позиция не сойдётся.')}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {CATS.map(c => (
          <button key={c.key} onClick={() => setCat(c.key)} style={chip(cat === c.key)}>
            {t(c.label)}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {SHOWS.map(s => (
          <button key={s.key} onClick={() => setShow(s.key)} style={chip(show === s.key)}>
            {t(s.label)}
          </button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Название, короткое имя или артикул…')}
          style={{
            flex: '1 1 240px', minWidth: 200, background: 'var(--bg)',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
            padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
          }} />
      </div>

      {loading ? (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('Загрузка...')}</p>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, textAlign: 'center', padding: '32px 20px', color: 'var(--muted)', fontSize: 13 }}>
          {show === 'negative'
            ? t('Минусовых остатков нет — сходится.')
            : t('Ничего не найдено')}
        </div>
      ) : (
        <>
          <div style={{ color: 'var(--dim)', fontSize: 12 }}>
            {t('Позиций')}: {count}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map(r => {
              const neg  = r.quantity < 0;
              const open = openRow === r.id;
              return (
                <div key={r.id} style={{
                  background: 'var(--bg)', borderRadius: 10,
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${neg ? '#ef4444' : 'var(--border)'}`,
                  padding: '11px 14px',
                  // Раскрытая строка отходит от соседей: без этого её разрез
                  // по складам читается как начало следующей позиции
                  ...openCard(open, neg ? '#ef4444' : 'var(--accent)'),
                }}>
                  <div
                    onClick={() => setOpenRow(open ? null : r.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 600, wordBreak: 'break-word' }}>
                        {r.name}
                        {/* Из каталога 1С позиция ушла, а листы на складе
                            остались. Остаток правдивый и его надо видеть;
                            закрыта только возможность взять новый заказ. */}
                        {r.gone_from_1c && (
                          <span style={{
                            marginLeft: 8, fontSize: 10.5, fontWeight: 700,
                            color: 'var(--muted)', border: '1px solid var(--border)',
                            borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap',
                          }}>{t('нет в каталоге 1С')}</span>
                        )}
                      </div>
                      <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 3 }}>
                        {r.guid_1c
                          ? `${t('код 1С')}: ${r.guid_1c}`
                          : <span style={{ color: '#f59e0b' }}>{t('нет кода 1С — сверять не по чему')}</span>}
                        {r.code ? ` · ${t('артикул')} ${r.code}` : ''}
                      </div>
                    </div>
                    <div style={{
                      color: neg ? '#ef4444' : 'var(--text)', fontWeight: 800,
                      fontSize: 15, whiteSpace: 'nowrap',
                    }}>
                      {fmtQty(r.quantity)} <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--dim)' }}>{r.unit}</span>
                    </div>
                    <span style={{ color: 'var(--dim)', fontSize: 12, width: 12 }}>{open ? '▾' : '▸'}</span>
                  </div>

                  {open && (
                    <div style={{ ...openDivider(neg ? '#ef4444' : 'var(--accent)'),
                                  display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(r.warehouses || []).length === 0 ? (
                        <div style={{ color: 'var(--dim)', fontSize: 12.5 }}>
                          {t('Движения по складам не было')}
                        </div>
                      ) : r.warehouses.map((w, i) => (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between', gap: 12,
                          background: 'var(--card)', borderRadius: 7, padding: '7px 11px',
                          fontSize: 12.5,
                        }}>
                          <span style={{ color: 'var(--text)', minWidth: 0 }}>
                            {w.name}
                            <span style={{ color: 'var(--dim)' }}>
                              {w.code ? ` · ${w.code}` : ` · ${t('без кода 1С')}`}
                            </span>
                          </span>
                          <b style={{ color: w.quantity < 0 ? '#ef4444' : 'var(--text)', whiteSpace: 'nowrap' }}>
                            {fmtQty(w.quantity)}
                          </b>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Страница ────────────────────────────────────────────────────────────────
export default function BuhgalterPage() {
  const [section, setSection] = useState('exchange');
  const [alerts, setAlerts]   = useState(0);

  // Число на пункте меню — единственный способ узнать про новую беду, не
  // заходя в раздел. Обмен идёт круглые сутки, а бухгалтер сидит в 1С.
  useEffect(() => {
    const load = () => integrationAPI.getHealth()
      .then(({ data }) => setAlerts((data.problems || []).length))
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const SECTIONS = [{
    title: 'Учёт',
    items: [
      { key: 'exchange', label: '⇆ Обмен с 1С', badge: alerts, alert: alerts > 0 },
      { key: 'recipes',  label: '🧾 Рецепты' },
      { key: 'subs',     label: '🔁 Замены' },
      // Значок был ▦ — геометрическая фигура, которая ничего не называет.
      // По всей программе остаток теперь помечен одним и тем же 📦.
      { key: 'balances', label: '📦 Остатки' },
    ],
  }];

  const TITLE = {
    exchange: 'Обмен с 1С',
    recipes:  'Рецепты',
    subs:     'Замены сырья',
    balances: 'Остатки',
  };

  return (
    <Layout title={t(TITLE[section])} sections={SECTIONS}
            section={section} onSection={setSection}>
      {section === 'exchange' && <ExchangeSection onGo={setSection} />}
      {section === 'recipes'  && <RecipesPage />}
      {section === 'subs'     && <SubstitutesPage />}
      {section === 'balances' && <BalancesSection />}
    </Layout>
  );
}
